"use client"

import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { createBlogReview } from "@/lib/actions/blog-reviews/post/createBlogReview"
import toast from "react-hot-toast"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { MessageSquarePlus, User } from "lucide-react"

const formSchema = z.object({
    author: z.string().min(2).max(50),
    comment: z.string().min(2).max(50),
})

interface AddReviewProps {
    blogId: string;
    slug: string;
}
const AddReview = ({
    blogId,
    slug
}: AddReviewProps) => {
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            author: "",
            comment: "",
        },
    })

    async function onSubmit(values: z.infer<typeof formSchema>) {
        console.log(values)
        const response = await createBlogReview({
            slug: slug,
            blogId,
            author: values.author,
            comment: values.comment
        })
        if (response.status == "200 OK") {
            form.reset
            toast.success("Thank You for your Review.")
        }
    }
    return (
        <Card className="w-full">
            <CardHeader className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-2xl">
                    <MessageSquarePlus className="h-6 w-6 text-primary" />
                    Share Your Thoughts
                </CardTitle>
                <CardDescription>
                    We'd love to hear your feedback on this blog post. Your review helps other readers!
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <FormField
                            control={form.control}
                            name="author"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <User className="h-4 w-4" />
                                        Your Name or Email
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            placeholder="e.g., John Doe or john@example.com"
                                            className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormDescription className="text-sm text-muted-foreground">
                                        This will be displayed publicly with your review.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="comment"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Your Review</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            placeholder="Share your thoughts about this blog post. What did you find most valuable? Any suggestions for improvement?"
                                            className="min-h-[120px] transition-all duration-200 focus:ring-2 focus:ring-primary/20 resize-none"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormDescription className="text-sm text-muted-foreground">
                                        {field.value?.length || 0}/500 characters
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button
                            type="submit"
                            className="w-fit bg-skillsewa-primary hover:bg-skillsewa-primary/90 transition-colors duration-200"
                            disabled={form.formState.isSubmitting}
                        >
                            {form.formState.isSubmitting ? "Submitting..." : "Submit Review"}
                        </Button>
                    </form>
                </Form>
            </CardContent>
        </Card>
    )
}

export default AddReview
